// src/components/Drive/AdminModal.jsx
import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  Tabs,
  Tab,
} from "@mui/material";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import { X, ClipboardCheck, PlusCircle, Settings } from "lucide-react";

import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import { useUser } from "../../context/UserContext";
import { useOrganization } from "../../hooks/useOrganization";
import { StatusPanel } from "./AdminPanels/StatusPanel";
import { CreatePanel } from "./AdminPanels/CreatePanel";
import { BusDeliveriesPanel } from "./AdminPanels/BusDeliveriesPanel";

const ADMIN_TABS = [
  { value: "deliveries", label: "Kuljetukset", icon: ClipboardCheck },
  { value: "create", label: "Luo kuljetus", icon: PlusCircle },
  { value: "bus", label: "Bussikuljetukset", icon: Settings },
];

export function AdminModal({ open, onClose, initialTab = "create" }) {
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const { colors } = useTheme();
  const { user } = useAuth();
  const { userData } = useUser();

  const organizationId = userData?.organizationId || null;
  const { organizationData, getMembers } = useOrganization(
    organizationId,
    user?.uid,
    userData
  );

  const [activeTab, setActiveTab] = useState(initialTab);

  // Get team members for assignment
  const members = getMembers();

  // Sync when parent changes which button was pressed
  React.useEffect(() => {
    if (open) setActiveTab(initialTab);
  }, [open, initialTab]);

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 0,
          px: isMobile ? 2 : 3,
          pt: isMobile ? 1.5 : 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Settings size={20} />
          <Typography variant="h6" fontWeight={700}>
            Admin
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>

      {/* ── Tab bar ── */}
      <Box
        sx={{
          px: isMobile ? 1.5 : 3,
          borderBottom: `1px solid ${
            colors.surface?.border || "rgba(0,0,0,0.12)"
          }`,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? "auto" : false}
          allowScrollButtonsMobile
          sx={{
            minHeight: 42,
            "& .MuiTab-root": {
              minHeight: 42,
              textTransform: "none",
              fontWeight: 600,
              px: 2,
            },
          }}
        >
          {ADMIN_TABS.map((t) => {
            const Icon = t.icon;
            return (
              <Tab
                key={t.value}
                value={t.value}
                icon={<Icon size={16} />}
                iconPosition="start"
                label={t.label}
              />
            );
          })}
        </Tabs>
      </Box>

      {/* ── Panel content ── */}
      {/* NOTE: DialogContent keeps its full top padding because the tab bar sits
          between it and DialogTitle, so we override it manually. */}
      <DialogContent
        sx={{
          pt: isMobile ? 1 : 1.5,
          px: isMobile ? 1.5 : 3,
          pb: isMobile ? 1.5 : 2.5,
        }}
      >
        {activeTab === "deliveries" && (
          <StatusPanel
            members={members}
            organizationData={organizationData}
          />
        )}
        {activeTab === "bus" && (
          <BusDeliveriesPanel
            members={members}
            organizationData={organizationData}
          />
        )}
        {activeTab === "create" && (
          <CreatePanel
            onClose={handleClose}
            members={members}
            organizationData={organizationData}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}